import { graphql, useStaticQuery } from 'gatsby';
import { useDeferredValue, useMemo } from 'react';
import { personalizationConditionMatches } from './component-personalization.validation';
import { useComponentPersonalizationContext } from './component-personalization.context';
import { type PersonalizationAttributes } from './component-personalization.schema';
import { logger } from '@sentry/gatsby';

type Writeable<T> = { -readonly [P in keyof T]: Writeable<T[P]> };

type PersonalizedPage =
  | Queries.GeneralPageTemplateQuery['kontentItemGeneralPage']
  | Queries.IndexPageTemplateQuery['allKontentItemIndexPage']['nodes'][number]
  | Queries.BlogTagIndexPageQuery['kontentItemIndexPage']
  | Queries.ArticlePageTemplateQuery['kontentItemArticlePage']
  | Queries.SearchPageQuery['kontentItemGeneralPage'];

type BuildingBlock = NonNullable<
  NonNullable<PersonalizedPage>['elements']['building_blocks']
>['value'][number];

const extractCampaignIds = (buildingBlock: BuildingBlock) => {
  const isPersonalizationsField = (
    value: unknown
  ): value is Queries.COMPONENT_PERSONALIZATIONFragment => {
    const personalizationField = 'component_a_b_test_personalization_snippet__personalizations';
    if (value === null || typeof value !== 'object') {
      return false;
    }

    return personalizationField in value;
  };

  return Object.entries(buildingBlock).reduce((campaignIds, [_, currentValue]) => {
    if (isPersonalizationsField(currentValue)) {
      campaignIds.push(
        ...(currentValue.component_a_b_test_personalization_snippet__personalizations?.value.map(
          (e) => e.elements.unique_id.value
        ) ?? [])
      );
    }

    return campaignIds;
  }, [] as string[]);
};

const isCampaignActive = (campaign: Queries.PERSONALIZATION_CAMPAIGNFragment) => {
  if (campaign.elements.enabled.value !== 'yes') {
    return false;
  }

  const startDate = campaign.elements.start_date?.value;
  const endDate = campaign.elements.end_date?.value;
  const now = new Date();

  if (typeof startDate === 'string' && new Date(startDate) > now) {
    return false;
  }

  if (typeof endDate === 'string' && new Date(endDate) < now) {
    return false;
  }

  return true;
};
/**
 * Evaluates if a user's attributes match a campaign's conditions.
 */
const doesCampaignMatch = (
  campaign: Queries.PERSONALIZATION_CAMPAIGNFragment,
  attributes: PersonalizationAttributes
): boolean => {
  const logicalOperator = campaign.elements.logical_operator.value;
  const conditions = campaign.elements.conditions.value;
  const campaignId = campaign.elements.unique_id.value;

  if (!conditions || conditions.length === 0) {
    logger.info('Component Personalization - Empty conditions');
    return false;
  }

  const check = (condition: Queries.PERSONALIZATION_CONDITIONFragment) =>
    personalizationConditionMatches({ condition, attributes });

  let returnValue = false;
  if (logicalOperator === 'and') {
    returnValue = conditions.every(check);
  }
  if (logicalOperator === 'or') {
    returnValue = conditions.some(check);
  }

  logger.info('Component Personalization - Campaign Evaluation', {
    campaignId,
    returnValue,
  });

  return returnValue;
};

/**
 * Finds the first active, matching personalized component for a building block.
 */
const getPersonalizedBlock = (
  buildingBlock: BuildingBlock,
  campaignsMap: Map<string, Queries.PERSONALIZATION_CAMPAIGNFragment>,
  attributes: PersonalizationAttributes
) => {
  const campaignIds = extractCampaignIds(buildingBlock);

  // skip processing if no campaigns are associated with the block
  if (campaignIds.length === 0) {
    return { block: buildingBlock, campaign: null };
  }

  logger.info('Component Personalization - Executing personalization for block', {
    block: buildingBlock.system.id,
    campaignIds,
  });

  // Find the first campaign that is active and its conditions match the user
  const winningCampaign = campaignIds
    .map((id) => campaignsMap.get(id)) // Get the full campaign object from the map
    .find((campaign) => {
      const matches =
        campaign && isCampaignActive(campaign) && doesCampaignMatch(campaign, attributes);

      logger.info('Component Personalization - Campaign execution result for block', {
        block: buildingBlock.system.id,
        campaign,
        matches,
      });
      return matches;
    });

  if (winningCampaign) {
    logger.info('Component Personalization - Returning personalized content for block', {
      block: buildingBlock.system.id,
      campaignId: winningCampaign.elements.unique_id.value,
      personalizedBlock: winningCampaign.elements.component_to_show.value.system.id,
    });
    return { block: winningCampaign.elements.component_to_show.value, campaign: winningCampaign };
  }

  logger.info('Component Personalization - No campaign match for block, returning original block', {
    block: buildingBlock.system.id,
    campaignIds,
  });

  // If no campaign matched, return the original block
  return { block: buildingBlock, campaign: null };
};

export const usePersonalization = (page: PersonalizedPage) => {
  const { allKontentItemPersonalizationCampaign } =
    useStaticQuery<Queries.PersonalizationCampaignListQuery>(graphql`
      query PersonalizationCampaignList {
        allKontentItemPersonalizationCampaign(filter: { preferred_language: { eq: "en" } }) {
          nodes {
            ...PERSONALIZATION_CAMPAIGN
          }
        }
      }
    `);

  const personalizationContext = useComponentPersonalizationContext();
  const attributes = useDeferredValue(personalizationContext.attributes);

  // 1. Create a fast-lookup map from the campaign data, memoized for performance.
  const campaignsMap = useMemo(() => {
    return new Map<string, Queries.PERSONALIZATION_CAMPAIGNFragment>(
      allKontentItemPersonalizationCampaign.nodes.map((campaign) => [
        campaign.elements.unique_id.value,
        campaign,
      ])
    );
  }, [allKontentItemPersonalizationCampaign.nodes]);

  // 2. Return the memoized personalized elements directly.
  return useMemo(() => {
    const buildingBlocks = page?.elements?.building_blocks?.value;

    // Early return in case no attributes or blocks exist
    if (!attributes || !buildingBlocks) {
      return {
        elements: page!.elements,
        winningCampaigns: [],
        attributes,
      };
    }

    const elements = { ...page.elements, building_blocks: { value: [] } } as Writeable<
      NonNullable<PersonalizedPage>['elements']
    >;

    const result = {
      elements,
      winningCampaigns: [] as Array<string>,
      attributes,
    };

    buildingBlocks.forEach((block) => {
      const { block: personalizedBlock, campaign } = getPersonalizedBlock(
        block,
        campaignsMap,
        attributes
      );

      if (campaign) {
        result.winningCampaigns.push(campaign.elements.unique_id.value);
      }

      result.elements!.building_blocks!.value.push(personalizedBlock);
    });

    return result;
  }, [page, attributes, campaignsMap]);
};

export default usePersonalization;
